import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

export default function NewButton({
  children,
  size = 'md',
  variant = 'solid',
  color = 'primary',
  isLoading = false,
  leftIcon = null,
  rightIcon = null,
  disabled = false,
  className = '',
  onClick,
  type = 'button',
  ...rest
}) {
  // Size styles
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-4 py-2 text-base gap-2',
    lg: 'px-6 py-3 text-lg gap-2.5',
  };

  // Color styles for solid variant
  const solidColorClasses = {
    primary:
      'bg-primary-500 hover:bg-primary-600 active:bg-primary-700 text-white',
    secondary:
      'bg-secondary-500 hover:bg-secondary-600 active:bg-secondary-700 text-white',
    gray: 'bg-gray-500 hover:bg-gray-600 active:bg-gray-700 text-white',
    danger: 'bg-danger-500 hover:bg-danger-600 active:bg-danger-700 text-white',
    success:
      'bg-success-500 hover:bg-success-600 active:bg-success-700 text-white',
    white:
      'bg-white/20 hover:bg-white/30 active:bg-white/40 text-white border-0',
  };

  // Color styles for bordered variant
  const borderedColorClasses = {
    primary:
      'border-2 border-primary-500 text-primary-600 hover:bg-primary-50 active:bg-primary-100',
    secondary:
      'border-2 border-secondary-500 text-secondary-600 hover:bg-secondary-50 active:bg-secondary-100',
    gray: 'border-2 border-gray-400 text-gray-700 hover:bg-gray-50 active:bg-gray-100',
    danger:
      'border-2 border-danger-500 text-danger-600 hover:bg-danger-50 active:bg-danger-100',
    success:
      'border-2 border-success-500 text-success-600 hover:bg-success-50 active:bg-success-100',
    white:
      'border-2 border-white text-white hover:bg-white/10 active:bg-white/20',
  };

  // Loading spinner sizes
  const spinnerSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const colorClass =
    variant === 'solid'
      ? solidColorClasses[color]
      : borderedColorClasses[color];

  const buttonClasses = classNames(
    // Base styles
    'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    // Size
    sizeClasses[size],
    // Color & variant
    colorClass,
    // Focus ring color
    {
      'focus:ring-primary-500': color === 'primary',
      'focus:ring-secondary-500': color === 'secondary',
      'focus:ring-gray-500': color === 'gray',
      'focus:ring-danger-500': color === 'danger',
      'focus:ring-success-500': color === 'success',
      'focus:ring-white': color === 'white',
    },
    // Disabled state
    {
      'opacity-50 cursor-not-allowed': disabled || isLoading,
      'hover:transform hover:scale-105 active:scale-95':
        !disabled && !isLoading,
    },
    // Custom className
    className,
  );

  const LoadingSpinner = () => (
    <svg
      className={classNames('animate-spin', spinnerSizes[size])}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || isLoading}
      {...rest}
    >
      {isLoading && <LoadingSpinner />}
      {!isLoading && leftIcon && (
        <span className="flex-shrink-0">{leftIcon}</span>
      )}
      {children && <span>{children}</span>}
      {!isLoading && rightIcon && (
        <span className="flex-shrink-0">{rightIcon}</span>
      )}
    </button>
  );
}

NewButton.propTypes = {
  children: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['solid', 'bordered']),
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'gray',
    'danger',
    'success',
    'white',
  ]),
  isLoading: PropTypes.bool,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
};
